import { type RefCallback, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { settleableOn, whenSettled } from './animations';

/**
 * KEEPING A SURFACE ALIVE UNTIL ITS EXIT HAS FINISHED (crm-design/spec/motion.md D5).
 *
 * A surface that is unmounted the moment it is closed has no exit: React takes the element
 * out of the tree and the browser never runs the closing keyframes. The usual answer in
 * this codebase was a NUMBER - `SheetNavigator`'s `SLIDE_OUT_MS = 280`, `useLoadingBlend`'s
 * `BLEND_MS = 240` - a copy of a duration that lives in tokens.css, kept in step by hand and
 * wrong the moment the token changes. Both of those numbers were written against durations
 * that no longer exist.
 *
 * SO THIS HOOK WAITS ON THE ANIMATIONS THEMSELVES. `element.getAnimations()` is the list the
 * browser is actually running on that element, and every entry carries a `finished` promise.
 * When they have all settled the element leaves. Nothing here reads a duration, so nothing
 * here can fall out of step with one, and an animation that was cancelled or interrupted
 * settles too - `allSettled`, not `all`, because a cancelled animation REJECTS and a surface
 * that stays mounted forever because its exit was interrupted is the worse failure.
 *
 * NOT EVERY ENTRY IN THAT LIST IS WORTH WAITING FOR, and the rule for which ones are is
 * `ui/motion/animations.ts`: an infinite animation (the skeleton's pulse, the spinner), a
 * paused one and one with no finite end never settle at all, so waiting on one of them keeps
 * the surface in the tree for the rest of the session. They are treated as absent.
 *
 * WITH NO ANIMATION TO WAIT FOR - a unit environment with no stylesheet, a family class whose
 * keyframes a media query switched off, a browser that has not started the animation yet -
 * the element leaves on the NEXT FRAME. A frame, not a timeout: it is the shortest wait that
 * still lets the browser paint the closed state once, and it needs no number either. An
 * element that is already OUT OF THE DOCUMENT leaves at once instead: there is nothing left
 * to paint and nothing that could still start an animation on it.
 *
 * WHAT A CONSUMER DOES WITH IT:
 *
 *   const { mounted, state, ref } = useMotion(open);
 *   if (!mounted) return null;
 *   return <div ref={ref} data-state={state} className={MOTION.rise}>…</div>;
 *
 * `data-state` is the attribute Radix already writes, so a component that is a Radix
 * consumer keeps Radix's own and a component that is not gets the same vocabulary.
 */

export type MotionState = 'open' | 'closed';

export interface UseMotionOptions {
  /**
   * TRACK THE ELEMENT'S POSITION BETWEEN COMMITS. On every commit the hook records where the
   * element is; when it has moved since the last one, it sets `translate` to the offset it
   * came FROM and clears it on the next frame, so the `motion-move` transition carries the
   * element from its old place to its new one. This is what a reordered row rides on.
   *
   * It is skipped under Reduce Motion: a move has no fade form, so the row appears in its new
   * place instead of travelling to it (spec section 9).
   */
  readonly layout?: boolean | undefined;
  /** Called once, after the exit has finished and the element has left. */
  readonly onExited?: (() => void) | undefined;
}

export interface Motion {
  /** Whether the consumer should render the element at all. */
  readonly mounted: boolean;
  /** The value for `data-state`; the family class reads it. */
  readonly state: MotionState;
  /** Put this on the element that carries the family class. */
  readonly ref: RefCallback<HTMLElement>;
}

/** The resolved travel token, or the OS setting when no stylesheet supplies it. */
function prefersReducedMotion(node: HTMLElement): boolean {
  // The gallery's two scopes use the same token as the OS substitution (spec section 9).
  // A resolved scope wins; without a stylesheet, retain the OS fallback.
  const travel = getComputedStyle(node).getPropertyValue('--duration-travel').trim();
  if (/^\d+(?:\.\d+)?m?s$/.test(travel)) return Number.parseFloat(travel) === 0;
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function useMotion(open: boolean, options: UseMotionOptions = {}): Motion {
  const { layout = false, onExited } = options;
  const [mounted, setMounted] = useState(open);
  const element = useRef<HTMLElement | null>(null);
  const previous = useRef<{ readonly left: number; readonly top: number } | null>(null);
  const exited = useRef(onExited);
  const interrupted = useRef({ x: 0, y: 0 });

  useEffect(() => {
    exited.current = onExited;
  });

  /**
   * ENTERING IS NOT DEFERRED. The element has to be in the tree on the same commit that
   * opened it, or the enter keyframes start a frame late and the first frame of the surface
   * is drawn at its resting appearance. Adjusting state during render is React's own answer
   * to "a prop changed and the state has to follow": it re-renders before anything is
   * committed, so the consumer below never sees the stale `false`.
   */
  if (open && !mounted) setMounted(true);

  const state: MotionState = open ? 'open' : 'closed';

  const ref = useCallback<RefCallback<HTMLElement>>((node) => {
    element.current = node;
    // A new element has no previous position; the next commit is its first measurement.
    if (node === null) previous.current = null;
  }, []);

  useEffect(() => {
    if (open || !mounted) return undefined;
    const node = element.current;
    let live = true;
    const leave = (): void => {
      if (!live) return;
      live = false;
      setMounted(false);
      exited.current?.();
    };

    /**
     * AN ELEMENT THAT IS NO LONGER IN THE DOCUMENT LEAVES IMMEDIATELY. Its animations were
     * cancelled when it was detached, nothing can start a new one on it, and there is no
     * frame in which anybody could see the closed state - so a wait here is a wait for
     * nothing, and on a detached subtree it can be a wait that never ends.
     */
    if (node !== null && !node.isConnected) {
      leave();
      return () => {
        live = false;
      };
    }

    /**
     * `getAnimations` is absent in the unit environment and on an element that never got
     * one; an infinite or paused animation is in the list but never settles. Both are the
     * same case as "there is nothing to wait for" (`animations.ts`).
     */
    const running = settleableOn(node);

    if (running.length === 0) {
      const frame = requestAnimationFrame(leave);
      return () => {
        live = false;
        cancelAnimationFrame(frame);
      };
    }

    void whenSettled(running).then(leave);
    return () => {
      live = false;
    };
  }, [open, mounted]);

  /**
   * NO DEPENDENCY ARRAY, and that is the contract: the position has to be read after EVERY
   * commit, because what moves a row is a change in its siblings, not a change in its own
   * props. A layout effect, so the read happens before the browser paints the new position
   * and the element never appears in its new place for one frame.
   */
  useLayoutEffect(() => {
    if (!layout) return undefined;
    const node = element.current;
    if (node === null) return undefined;

    // Disabling transitions BEFORE measuring is essential: clearing translate while a
    // transition is active changes its target, but leaves its interpolated position live.
    const carried = node.style.translate || '';
    const priorTransition = node.style.transition || '';
    node.style.transition = 'none';
    node.style.translate = '';
    const unshifted = node.getBoundingClientRect();
    node.style.translate = carried;
    const rect = node.getBoundingClientRect();
    node.style.transition = priorTransition;

    const last = previous.current;
    const residual = interrupted.current;
    interrupted.current = { x: 0, y: 0 };
    previous.current = { left: rect.left, top: rect.top };
    if (last === null || prefersReducedMotion(node)) return undefined;

    const dx = last.left - rect.left + residual.x;
    const dy = last.top - rect.top + residual.y;
    if (dx === 0 && dy === 0) return undefined;

    // Coordinates come from layout, not motion values. The family supplies all timing.
    node.style.transition = 'none';
    node.style.translate = `${dx + rect.left - unshifted.left}px ${dy + rect.top - unshifted.top}px`;
    // Flush the inversion before the next frame restores the family's transition.
    void node.getBoundingClientRect().top;
    const release = (): void => {
      node.style.transition = priorTransition;
      node.style.translate = carried;
    };
    const frame = requestAnimationFrame(release);
    return () => {
      cancelAnimationFrame(frame);
      // A commit can interrupt either the inversion frame or the running transition.
      // Save its visual displacement relative to the target, independent of layout.
      const visible = node.getBoundingClientRect();
      node.style.transition = 'none';
      node.style.translate = carried;
      const target = node.getBoundingClientRect();
      interrupted.current = { x: visible.left - target.left, y: visible.top - target.top };
      node.style.transition = priorTransition;
    };
  });

  return { mounted, state, ref };
}
